/** @typedef {'additive' | 'absolute'} QuotaValueMode */

export function quotaOptionRank(value) {
  if (value === null || value === undefined) return Number.POSITIVE_INFINITY;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function isStrictQuotaUpgrade(optionValue, planDefault) {
  return quotaOptionRank(optionValue) > quotaOptionRank(planDefault);
}

export function planQuotaBase(planDefault) {
  if (planDefault === null || planDefault === undefined) return null;
  return planDefault;
}

export function quotaValueMode(pkg) {
  return pkg?.valueMode === 'additive' ? 'additive' : 'absolute';
}

export function effectiveQuotaFromStoreOption(planDefault, optionValue, mode) {
  if (mode === 'absolute') return optionValue;
  if (optionValue === null) return null;
  const base = planQuotaBase(planDefault);
  if (base === null) return null;
  return base + optionValue;
}

export function findStoreQuotaOption(pkg, effective, planDefault) {
  const mode = quotaValueMode(pkg);
  const base = planQuotaBase(planDefault);

  for (const opt of pkg.options || []) {
    if (mode === 'additive') {
      if (opt.value === null && effective === null && base !== null) return opt;
      if (
        opt.value != null &&
        base != null &&
        effective != null &&
        base + opt.value === effective
      ) {
        return opt;
      }
      continue;
    }
    if (opt.value === effective) return opt;
  }
  return null;
}

export function findPlanIncludedStoreOption(pkg, planDefault) {
  if (planDefault === null || planDefault === undefined) {
    const unlimited = pkg.options?.find((o) => o.value === null);
    if (unlimited) return unlimited;
    return { value: null, label: 'Unlimited (plan)', priceMonthly: 0 };
  }
  const exact = pkg.options?.find((o) => o.value === planDefault);
  if (exact) return exact;
  if (quotaValueMode(pkg) === 'absolute') {
    return { value: planDefault, label: `${planDefault} (plan)`, priceMonthly: 0 };
  }
  return null;
}

export function partitionStoreQuotaOptions(pkg, planDefault) {
  if (!pkg.planLocked) {
    const base = planQuotaBase(planDefault);
    const upgrades =
      quotaValueMode(pkg) === 'additive' && base === null
        ? []
        : pkg.options || [];
    return { planTier: null, upgrades };
  }
  const planTier = findPlanIncludedStoreOption(pkg, planDefault);
  const base = planQuotaBase(planDefault);
  if (base === null) {
    return { planTier, upgrades: [] };
  }
  const upgrades = (pkg.options || []).filter((o) =>
    isStrictQuotaUpgrade(o.value, planDefault),
  );
  return { planTier, upgrades };
}

export function quotaAddonMonthlyAmount(pkg, effective, planDefault) {
  const opt = findStoreQuotaOption(pkg, effective, planDefault);
  if (!opt || opt.priceMonthly <= 0) return 0;
  if (quotaValueMode(pkg) === 'absolute' && effective === planDefault) return 0;
  if (quotaValueMode(pkg) === 'additive' && effective === planDefault) return 0;
  return opt.priceMonthly;
}

export function formatPlanQuotaValue(value, locale) {
  if (value === null || value === undefined) {
    return locale?.quotaUnlimited ?? '∞';
  }
  return String(value);
}

export function formatQuotaCellDisplay(code, value, locale) {
  if (code === 'trend_max_tags') {
    if (value === null || value === undefined) return formatPlanQuotaValue(value, locale);
    return `${value} ${locale?.quotaTagsSuffix ?? 'tags'}`;
  }
  if (code === 'alarm_retention_days' || code === 'trend_retention_days') {
    if (value === null || value === undefined) return formatPlanQuotaValue(value, locale);
    return `${value} ${locale?.quotaDaysSuffix ?? 'days'}`;
  }
  if (code === 'command_priority_ceiling' && value != null) {
    return `${locale?.quotaPriorityPrefix ?? 'up to'} ${value}`;
  }
  return formatPlanQuotaValue(value, locale);
}

export function formatQuotaOptionHint(pkg, opt, planDefault, locale) {
  const mode = quotaValueMode(pkg);
  const base = planQuotaBase(planDefault);
  if (mode === 'additive') {
    if (base === null) return opt.label;
    const total = effectiveQuotaFromStoreOption(planDefault, opt.value, mode);
    const totalStr =
      total === null
        ? locale?.quotaUnlimited ?? '∞'
        : String(total);
    const baseStr = String(base);
    return `${opt.label} → ${totalStr} (${locale?.quotaPlanIncludes ?? 'plan'} ${baseStr})`;
  }
  return opt.label;
}
